<template>
  <div>
    <Toast />
    <BackButton to="/settings" />
    <div class="flex justify-end mb-4">
      <Button label="Nova Conta" @click="showAddAccountModal = true">
        <template #icon>
          <span class="material-icons">add</span>
        </template>
      </Button>
    </div>
    <AccountsTable
      :accounts="store.accounts"
      @delete="handleDeleteAccount"
      @configure-workflow="handleConfigureWorkflow"
      @edit-regulators="handleEditRegulators"
      :pending-platform-countries="pendingPlatformCountries"
      @set-platform-country="handleSetPlatformCountry"
      @manage-billing="handleManageBilling"
      @manage-kyx-pricing="handleManageKyxPricing"
    />
    <AddAccountModal
      v-model:visible="showAddAccountModal"
      @create="handleCreateAccount"
    />
    <EditAccountRegulatorsDialog
      v-model:visible="showRegulatorsDialog"
      :account="regulatorsAccount"
      :regulators="regulatorsAccountOptions"
      :saving="savingRegulators"
      @save="handleSaveRegulators"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useSystemAdminAccountStore } from '@/stores/systemAdminAccountStore';
import AccountsTable from '@/components/admin/AccountsTable.vue';
import EditAccountRegulatorsDialog from '@/components/admin/EditAccountRegulatorsDialog.vue';
import BackButton from '@/components/base/BackButton.vue';
import Button from 'primevue/button';
import AddAccountModal from '@/components/admin/AddAccountModal.vue';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import regulatorsApi from '@/api/modules/regulators';
import type { Account } from '@/types/account';
import type { Regulator } from '@/types/regulators';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { PLATFORM_COUNTRIES, type PlatformCountry } from '@/platformCountry';

const store = useSystemAdminAccountStore();
const router = useRouter();
const toast = useToast();
const { confirm } = useConfirmDialog();
const showAddAccountModal = ref(false);

// Regulator options for the edit dialog: the catalog of the account's platform country.
const regulatorsAccountOptions = ref<Regulator[]>([]);
const showRegulatorsDialog = ref(false);
const regulatorsAccount = ref<Account | null>(null);
const savingRegulators = ref(false);

// Platform picked per account (by id) while its confirmation or save is in flight.
const pendingPlatformCountries = ref<Record<string, PlatformCountry>>({});

onMounted(() => {
  store.fetchAccounts();
});

const handleCreateAccount = async (data: {
  name: string;
  cnpj: string;
  platform_country_code: PlatformCountry;
  regulators?: string[];
}) => {
  const { regulators, ...accountData } = data;
  try {
    const newAccount = await store.createAccount(accountData);
    if (newAccount && regulators && regulators.length) {
      await store.setAccountRegulators(newAccount.id, regulators);
    }
    toast.add({
      severity: 'success',
      summary: 'Conta criada',
      detail: 'A conta foi criada com sucesso.',
      life: 4000,
    });
  } catch (e: any) {
    toast.add({
      severity: 'error',
      summary: 'Erro ao criar conta',
      detail: 'Não foi possível criar a conta.',
      life: 5000,
    });
  }
};

// Setting the platform of an account that has none (e.g. created by SSO) is final: confirm first.
const handleSetPlatformCountry = (account: Account, platformCountryCode: PlatformCountry) => {
  if (account.platform_country_code !== null) return;
  const targetName = PLATFORM_COUNTRIES[platformCountryCode].name;
  // The row's picker shows the pending choice; clearing it reverts the picker unless the save landed.
  pendingPlatformCountries.value[account.id] = platformCountryCode;
  const clearPending = () => {
    delete pendingPlatformCountries.value[account.id];
  };
  confirm.require({
    header: 'Definir plataforma',
    message: `Definir a plataforma da conta "${account.name}" como ${targetName}? Depois de definida, a plataforma não poderá ser alterada.`,
    rejectLabel: 'Cancelar',
    acceptLabel: 'Definir',
    reject: clearPending,
    onHide: clearPending,
    accept: async () => {
      try {
        await store.setAccountPlatformCountry(account.id, platformCountryCode);
        toast.add({
          severity: 'success',
          summary: 'Plataforma definida',
          detail: `A conta agora está na plataforma ${targetName}.`,
          life: 4000,
        });
      } catch {
        toast.add({
          severity: 'error',
          summary: 'Erro ao salvar',
          detail: 'Não foi possível definir a plataforma da conta.',
          life: 5000,
        });
      } finally {
        clearPending();
      }
    },
  });
};

const handleDeleteAccount = (accountId: string) => {
  store.deleteAccount(accountId);
};

const handleConfigureWorkflow = (account: any) => {
  router.push({ name: 'system-admin-account-workflow', params: { id: account.id } });
};

const handleManageBilling = (account: any) => {
  router.push({ name: 'system-admin-account-billing', params: { id: account.id } });
};

const handleManageKyxPricing = (account: any) => {
  router.push({ name: 'system-admin-account-kyx-pricing', params: { id: account.id } });
};

const handleEditRegulators = async (account: Account) => {
  if (account.platform_country_code === null) {
    toast.add({
      severity: 'warn',
      summary: 'Plataforma não definida',
      detail: 'Defina a plataforma da conta antes de escolher os reguladores.',
      life: 5000,
    });
    return;
  }
  try {
    regulatorsAccountOptions.value = await regulatorsApi.list(account.platform_country_code);
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Erro ao carregar',
      detail: 'Não foi possível carregar os reguladores da plataforma.',
      life: 5000,
    });
    return;
  }
  regulatorsAccount.value = account;
  showRegulatorsDialog.value = true;
};

const handleSaveRegulators = async (accountId: string, slugs: string[]) => {
  savingRegulators.value = true;
  try {
    await store.setAccountRegulators(accountId, slugs);
    showRegulatorsDialog.value = false;
    toast.add({
      severity: 'success',
      summary: 'Reguladores atualizados',
      detail: 'Os reguladores da conta foram salvos.',
      life: 4000,
    });
  } catch (e: any) {
    toast.add({
      severity: 'error',
      summary: 'Erro ao salvar',
      detail: e.message || 'Não foi possível salvar os reguladores.',
      life: 5000,
    });
  } finally {
    savingRegulators.value = false;
  }
};
</script>
